'use client'

import { useEffect, useRef, useState } from 'react'
import { FOREIGN_WORKERS, WORKING_AGE } from '@/data/japan-stats'
import { useInView } from './useInView'

type Locale = 'en' | 'ja'
const GREEN = '#0E4B3B'
const RED = '#BB143A'
const GRID = '#E4E7EA'
const INK_MUTED = '#6B7280'

function useWidth(ref: React.RefObject<HTMLDivElement>) {
  const [w, setW] = useState(560)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const ro = new ResizeObserver(([e]) => setW(Math.max(260, Math.round(e.contentRect.width))))
    ro.observe(el)
    return () => ro.disconnect()
  }, [ref])
  return w
}

const fmtM = (v: number, l: Locale) => (l === 'ja' ? `${Math.round(v / 10000)}万` : `${(v / 1e6).toFixed(2)}M`)

/** Column chart: foreign workers in Japan, 2015–2025 (MHLW). */
export function ForeignWorkersChart({ locale }: { locale: Locale }) {
  const box = useRef<HTMLDivElement>(null)
  const inView = useInView(box, 0.3)
  const w = useWidth(box)
  const [hover, setHover] = useState<number | null>(null)
  const h = 250
  const m = { t: 28, r: 8, b: 26, l: 40 }
  const iw = w - m.l - m.r
  const ih = h - m.t - m.b
  const max = 3_000_000
  const n = FOREIGN_WORKERS.length
  const band = iw / n
  const bw = Math.min(24, band * 0.62)
  const y = (v: number) => m.t + ih - (v / max) * ih
  const ticks = [0, 1_000_000, 2_000_000, 3_000_000]
  const last = FOREIGN_WORKERS[n - 1]
  const hv = hover !== null ? FOREIGN_WORKERS[hover] : null

  return (
    <div ref={box} className="relative w-full">
      <svg width={w} height={h} role="img" aria-label={locale === 'ja' ? '日本の外国人労働者数の推移 2015〜2025年' : 'Foreign workers in Japan, 2015 to 2025'} className="block">
        {ticks.map((t) => (
          <g key={t}>
            <line x1={m.l} x2={w - m.r} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
            <text x={m.l - 8} y={y(t) + 4} textAnchor="end" fontSize={11} fill={INK_MUTED}>
              {locale === 'ja' ? `${t / 10000}万` : `${t / 1e6}M`}
            </text>
          </g>
        ))}
        {FOREIGN_WORKERS.map((d, i) => {
          const x = m.l + band * i + (band - bw) / 2
          const top = y(d.value)
          const height = m.t + ih - top
          const isLast = i === n - 1
          return (
            <g key={d.year} onPointerEnter={() => setHover(i)} onPointerLeave={() => setHover(null)}>
              <rect x={m.l + band * i} y={m.t} width={band} height={ih} fill="transparent" />
              <path
                d={`M${x} ${m.t + ih} V${top + 4} q0 -4 4 -4 h${bw - 8} q4 0 4 4 V${m.t + ih}Z`}
                fill={isLast ? RED : GREEN}
                opacity={hover === null || hover === i ? 1 : 0.45}
                style={{
                  transformOrigin: `0 ${m.t + ih}px`,
                  transform: inView ? 'scaleY(1)' : 'scaleY(0)',
                  transition: `transform 900ms cubic-bezier(.16,1,.3,1) ${i * 70}ms, opacity 200ms`,
                }}
              />
              {(i % 2 === 0 || isLast) && (
                <text x={x + bw / 2} y={h - 8} textAnchor="middle" fontSize={11} fill={INK_MUTED}>
                  {w < 420 ? `’${String(d.year).slice(2)}` : d.year}
                </text>
              )}
            </g>
          )
        })}
        <text
          x={m.l + band * (n - 1) + band / 2}
          y={y(last.value) - 8}
          textAnchor="end"
          fontSize={12}
          fontWeight={700}
          fill="#14213D"
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 400ms 1.2s' }}
        >
          {fmtM(last.value, locale)}
        </text>
      </svg>
      {hv && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-brand-navy px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: m.l + band * hover! + band / 2, top: y(hv.value) - 6 }}
        >
          <b className="block text-sm">{hv.value.toLocaleString('en-US')}</b>
          {locale === 'ja' ? `${hv.year}年10月末` : `October ${hv.year}`}
        </div>
      )}
      <table className="sr-only">
        <caption>{locale === 'ja' ? '日本の外国人労働者数' : 'Foreign workers in Japan'}</caption>
        <tbody>
          {FOREIGN_WORKERS.map((d) => (
            <tr key={d.year}>
              <th scope="row">{d.year}</th>
              <td>{d.value.toLocaleString('en-US')}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/** Line chart: Japan's working-age population (15–64), actual + projected. */
export function WorkingAgeChart({ locale }: { locale: Locale }) {
  const box = useRef<HTMLDivElement>(null)
  const inView = useInView(box, 0.3)
  const w = useWidth(box)
  const [hover, setHover] = useState<number | null>(null)
  const h = 250
  const m = { t: 28, r: 18, b: 26, l: 34 }
  const iw = w - m.l - m.r
  const ih = h - m.t - m.b
  const x0 = 1995
  const x1 = 2070
  const yMin = 40
  const yMax = 90
  const x = (yr: number) => m.l + ((yr - x0) / (x1 - x0)) * iw
  const y = (v: number) => m.t + ih - ((v - yMin) / (yMax - yMin)) * ih
  const pts = WORKING_AGE.map((d) => [x(d.year), y(d.value)] as const)
  const splitIdx = WORKING_AGE.findIndex((d) => d.projected) - 1
  const actual = pts.slice(0, splitIdx + 1)
  const projected = pts.slice(splitIdx)
  const toPath = (p: readonly (readonly [number, number])[]) => p.map(([a, b], i) => `${i ? 'L' : 'M'}${a} ${b}`).join(' ')
  const area = `${toPath(pts)} L${pts[pts.length - 1][0]} ${m.t + ih} L${pts[0][0]} ${m.t + ih}Z`
  const first = WORKING_AGE[0]
  const lastD = WORKING_AGE[WORKING_AGE.length - 1]
  const hv = hover !== null ? WORKING_AGE[hover] : null

  const onMove = (e: React.PointerEvent<SVGRectElement>) => {
    const r = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect()
    const px = e.clientX - r.left
    let best = 0
    pts.forEach(([a], i) => {
      if (Math.abs(a - px) < Math.abs(pts[best][0] - px)) best = i
    })
    setHover(best)
  }

  return (
    <div ref={box} className="relative w-full">
      <svg width={w} height={h} role="img" aria-label={locale === 'ja' ? '日本の生産年齢人口（15〜64歳）の推移と予測' : "Japan's working-age population, 1995 to 2070 (projected)"} className="block">
        {[40, 60, 80].map((t) => (
          <g key={t}>
            <line x1={m.l} x2={w - m.r} y1={y(t)} y2={y(t)} stroke={GRID} strokeWidth={1} />
            <text x={m.l - 6} y={y(t) + 4} textAnchor="end" fontSize={11} fill={INK_MUTED}>
              {locale === 'ja' ? `${t * 100}万` : `${t}M`}
            </text>
          </g>
        ))}
        {/* "projection" band */}
        <rect x={x(2020)} y={m.t} width={x(2070) - x(2020)} height={ih} fill="#BB143A" opacity={0.04} />
        <text x={x(2045)} y={m.t - 10} textAnchor="middle" fontSize={11} fill={INK_MUTED}>
          {locale === 'ja' ? '予測' : 'Projection'}
        </text>
        <line x1={x(2020)} x2={x(2020)} y1={m.t} y2={m.t + ih} stroke={GRID} strokeWidth={1} />
        <path d={area} fill={RED} opacity={inView ? 0.08 : 0} style={{ transition: 'opacity 800ms 600ms' }} />
        <path
          d={toPath(actual)}
          fill="none"
          stroke={GREEN}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          pathLength={1}
          strokeDasharray="1 1"
          style={{ strokeDashoffset: inView ? 0 : 1, transition: 'stroke-dashoffset 900ms cubic-bezier(.4,0,.2,1)' }}
        />
        <path
          d={toPath(projected)}
          fill="none"
          stroke={RED}
          strokeWidth={2.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeDasharray="6 6"
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 700ms 900ms' }}
        />
        {pts.map(([a, b], i) => (
          <circle
            key={i}
            cx={a}
            cy={b}
            r={hover === i ? 6 : 4}
            fill={WORKING_AGE[i].projected ? RED : GREEN}
            stroke="#fff"
            strokeWidth={2}
            style={{ opacity: inView ? 1 : 0, transition: `opacity 300ms ${300 + i * 120}ms` }}
          />
        ))}
        <text x={pts[0][0] + 8} y={pts[0][1] - 10} fontSize={12} fontWeight={700} fill="#14213D">
          {locale === 'ja' ? `${first.year}年 ${Math.round(first.value * 100)}万人` : `${first.value}M in ${first.year}`}
        </text>
        <text
          x={pts[pts.length - 1][0] - 6}
          y={pts[pts.length - 1][1] + 20}
          textAnchor="end"
          fontSize={12}
          fontWeight={700}
          fill="#14213D"
          style={{ opacity: inView ? 1 : 0, transition: 'opacity 400ms 1.6s' }}
        >
          {locale === 'ja' ? `${lastD.year}年 ${Math.round(lastD.value * 100)}万人` : `${lastD.value}M by ${lastD.year}`}
        </text>
        {WORKING_AGE.filter((d) => [1995, 2020, 2040, 2070].includes(d.year)).map((d) => (
          <text key={d.year} x={x(d.year)} y={h - 8} textAnchor="middle" fontSize={11} fill={INK_MUTED}>
            {d.year}
          </text>
        ))}
        {hv && <line x1={pts[hover!][0]} x2={pts[hover!][0]} y1={m.t} y2={m.t + ih} stroke="#14213D" strokeOpacity={0.25} />}
        <rect x={m.l} y={m.t} width={iw} height={ih} fill="transparent" onPointerMove={onMove} onPointerLeave={() => setHover(null)} />
      </svg>
      {hv && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg bg-brand-navy px-3 py-2 text-xs text-white shadow-lg"
          style={{ left: Math.min(Math.max(pts[hover!][0], 70), w - 70), top: pts[hover!][1] - 10 }}
        >
          <b className="block text-sm">{locale === 'ja' ? `${Math.round(hv.value * 100)}万人` : `${hv.value} million`}</b>
          {hv.year}
          {hv.projected ? (locale === 'ja' ? '（予測）' : ' (projected)') : ''}
        </div>
      )}
      <table className="sr-only">
        <caption>{locale === 'ja' ? '生産年齢人口（百万人）' : 'Working-age population (millions)'}</caption>
        <tbody>
          {WORKING_AGE.map((d) => (
            <tr key={d.year}>
              <th scope="row">{d.year}</th>
              <td>
                {d.value}
                {d.projected ? ' (projected)' : ''}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
